import React from 'react';
import { Typography, Box } from '@mui/material';
import { useTypewriter } from '../../hooks/useTypewriter';

const stripHtml = (value = '') => value.replace(/<\/?[^>]+(>|$)/gi, '');

const parseStrongTags = (text = '') => {
  if (!text) {
    return { plainText: '', strongRanges: [] };
  }

  const regex = /<strong>(.*?)<\/strong>/gi;
  let match;
  let cursor = 0;
  let plainText = '';
  const strongRanges = [];

  while ((match = regex.exec(text)) !== null) {
    const before = text.slice(cursor, match.index);
    plainText += stripHtml(before);

    const boldContent = stripHtml(match[1] ?? '');
    const start = plainText.length;
    plainText += boldContent;
    const end = plainText.length;

    if (boldContent) {
      strongRanges.push({ start, end });
    }

    cursor = regex.lastIndex;
  }

  const trailing = text.slice(cursor);
  plainText += stripHtml(trailing);

  return { plainText, strongRanges };
};

/**
 * AnimatedTypography Component
 * A wrapper around MUI Typography that adds typewriter animation effect
 * Maintains all Typography props while adding typing animation capabilities
 */
const AnimatedTypography = ({ 
  children, 
  showCursor = false,
  typewriterSpeed = 30,
  startDelay = 0,
  wordMode = false,
  enabled = true,
  fadeIn = true, // New prop for fade-in effect
  preserveStrong = false,
  ...typographyProps 
}) => {
  // Extract text content from children (string or React element with string content)
  const getTextContent = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    if (React.isValidElement(content) && typeof content.props.children === 'string') {
      return content.props.children;
    }
    // For more complex children, try to extract text
    if (Array.isArray(content)) {
      return content.map(getTextContent).join('');
    }
    return String(content || '');
  };

  const fullText = getTextContent(children);
  const { plainText, strongRanges } = React.useMemo(() => {
    if (!preserveStrong) {
      return { plainText: fullText, strongRanges: [] };
    }
    return parseStrongTags(fullText);
  }, [fullText, preserveStrong]);
  
  const { displayText, isTyping, isDone, currentlyTypingIndex } = useTypewriter(plainText, {
    speed: typewriterSpeed,
    startDelay,
    enabled,
    wordMode
  });

  // Split text into characters for individual styling
  const renderTextWithFade = () => {
    if (!displayText) return '';
    
    return displayText.split('').map((char, index) => {
      const isBold = strongRanges.some(
        (range) => index >= range.start && index < range.end
      );
      // Check if this is the currently being typed character (last few chars)
      const isCurrentlyTyping = isTyping && index >= currentlyTypingIndex - 2 && index <= currentlyTypingIndex;
      
      return (
        <span
          key={index}
          style={{
            fontWeight: isBold ? 600 : 'inherit',
            opacity: isCurrentlyTyping ? 0.6 : 1,
            color: isCurrentlyTyping ? '#9e9e9e' : 'inherit',
            transition: 'opacity 0.2s ease-out, color 0.2s ease-out',
            textShadow: isCurrentlyTyping ? '0 0 2px rgba(158, 158, 158, 0.3)' : 'none'
          }}
        >
          {char}
        </span>
      );
    });
  };

  // Calculate opacity based on text progress for smooth fade-in
  const opacity = fadeIn && enabled ? (isDone ? 1 : 0.95) : 1;

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      flexWrap: 'wrap',
      opacity,
      transition: 'opacity 0.3s ease-in-out'
    }}>
      <Typography 
        {...typographyProps}
        sx={{
          ...typographyProps.sx,
          // Add smooth letter-spacing animation for more graceful appearance
          letterSpacing: isTyping ? '0.02em' : 'normal',
          transition: 'letter-spacing 0.2s ease-out'
        }}
      >
        {renderTextWithFade()}
      </Typography>
      {showCursor && isTyping && (
        <Typography
          component="span"
          sx={{
            ...typographyProps.sx,
            animation: 'blink 1s infinite',
            marginLeft: '2px',
            opacity: 0.7,
            '@keyframes blink': {
              '0%, 50%': { opacity: 0.7 },
              '51%, 100%': { opacity: 0 }
            }
          }}
        >
          |
        </Typography>
      )}
    </Box>
  );
};

export default AnimatedTypography;
